import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth, roleHome } from "../../context/AuthContext";
import { AuthShell } from "./AuthShell";

const EXPERIENCE = [
  ["none", "No experience"],
  ["beginner", "Beginner (< 1 year)"],
  ["intermediate", "Intermediate (1-3 years)"],
  ["advanced", "Advanced (3-5 years)"],
  ["professional", "Professional (5+ years)"],
];

const APPETITE = [
  ["low", "Low — conservative growth"],
  ["medium", "Medium — balanced"],
  ["high", "High — aggressive"],
];

export default function Onboarding() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", country: "",
    profession: "", trading_experience: "beginner",
    current_equity: "", total_trading_losses: "",
    trading_goals: "", risk_appetite: "medium", referred_by: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["first_name", "last_name", "phone", "country", "profession", "trading_goals"]) {
      if (!form[k].trim()) return toast.error(`Please fill ${k.replace("_", " ")}`);
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        current_equity: parseFloat(form.current_equity) || 0,
        total_trading_losses: parseFloat(form.total_trading_losses) || 0,
        referred_by: form.referred_by || null,
      };
      const { data } = await api.post("/auth/onboarding", payload);
      setUser(data);
      toast.success("Welcome to ChartzFx!");
      navigate(roleHome(data.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save details");
    } finally { setBusy(false); }
  };

  const bind = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <AuthShell title="One last step" subtitle="Tell us a bit about yourself so we can personalise your trading experience.">
      <form onSubmit={submit} className="space-y-4" data-testid="onboarding-full-form">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-xs block mb-1">First Name</label><input data-testid="ob-first-name" className="input-dark" required {...bind("first_name")} /></div>
          <div><label className="label-xs block mb-1">Last Name</label><input data-testid="ob-last-name" className="input-dark" required {...bind("last_name")} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-xs block mb-1">Phone (with country code)</label><input data-testid="ob-phone" className="input-dark" placeholder="+91 98765 43210" required {...bind("phone")} /></div>
          <div><label className="label-xs block mb-1">Country</label><input data-testid="ob-country" className="input-dark" required {...bind("country")} /></div>
        </div>
        <div><label className="label-xs block mb-1">Profession</label><input data-testid="ob-profession" className="input-dark" placeholder="Software engineer" required {...bind("profession")} /></div>
        <div>
          <label className="label-xs block mb-1">Trading Experience</label>
          <select data-testid="ob-experience" className="input-dark" {...bind("trading_experience")}>
            {EXPERIENCE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-xs block mb-1">Current Equity (USD)</label><input data-testid="ob-equity" type="number" min="0" step="1" className="input-dark" placeholder="10000" {...bind("current_equity")} /></div>
          <div><label className="label-xs block mb-1">Total Past Losses (USD)</label><input data-testid="ob-losses" type="number" min="0" step="1" className="input-dark" placeholder="0" {...bind("total_trading_losses")} /></div>
        </div>
        <div><label className="label-xs block mb-1">Trading Goals</label>
          <textarea data-testid="ob-goals" className="input-dark min-h-[80px]" placeholder="What do you want to achieve?" required {...bind("trading_goals")} /></div>
        <div>
          <label className="label-xs block mb-1">Risk Appetite</label>
          <select data-testid="ob-appetite" className="input-dark" {...bind("risk_appetite")}>
            {APPETITE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div><label className="label-xs block mb-1">Referred By (optional)</label><input data-testid="ob-referral" className="input-dark" placeholder="Referrer email or code" {...bind("referred_by")} /></div>
        <button data-testid="ob-submit-button" disabled={busy} className="btn-primary w-full py-2.5 text-sm font-medium disabled:opacity-50">
          {busy ? "Saving…" : "Complete Onboarding"}
        </button>
      </form>
    </AuthShell>
  );
}
